import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { SalePhoto } from './sale-photo';
export { SalePhoto };
import { salePhotoSchema } from '@schemas/business-objects/sale-photo';
import { MeasurementSetFormArray } from './measurement-set-form-group';
import { SaleFormGroup } from './sale-form-group';

export class SalePhotoFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    hipNumber: SchemaFormControl;
    sex: SchemaFormControl;
    sire: SchemaFormControl;
    dam: SchemaFormControl;
    consignor: SchemaFormControl;
    imageData: SchemaFormControl;
    contentType: SchemaFormControl;
    imageWidth: SchemaFormControl;
    imageHeight: SchemaFormControl;
    saleId: SchemaFormControl;
    sale: SaleFormGroup;
    measurementSets: MeasurementSetFormArray;
  };

  declare value: Partial<SalePhoto>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(salePhotoSchema, SalePhoto, data, options);
  }

  get object(): SalePhoto {
    return new SalePhoto(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'sale':
        return new SaleFormGroup();
      case 'measurementSets':
        return new MeasurementSetFormArray();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<SalePhoto[]> {
    return SalePhoto.find(filter);
  }

  static async findOne(filter?: any): Promise<SalePhoto | null> {
    return SalePhoto.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<SalePhoto> {
    return SalePhoto.findById(id, filter);
  }

  static async create(data: Partial<SalePhoto>): Promise<SalePhoto> {
    return SalePhoto.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<SalePhoto>,
  ): Promise<SalePhoto> {
    return SalePhoto.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return SalePhoto.deleteById(id);
  }

  async extractBannerDetails(overwrite?: boolean): Promise<SalePhotoFormGroup> {
    const instance = new SalePhoto(this.getRawValue());
    const result = await instance.extractBannerDetails(overwrite);
    const fg = new SalePhotoFormGroup();
    fg._populate(result);
    return fg;
  }
}

export class SalePhotoFormArray extends PersistedFormArray {
  readonly entityName = 'SalePhoto' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(salePhotoSchema, SalePhoto, options);
  }

  protected override createItemGroup() {
    return new SalePhotoFormGroup();
  }

  get array(): SalePhoto[] {
    return this.controls.map((group: SalePhotoFormGroup) => group.object);
  }
}

export class SalePhotoPersistedArray extends PersistedArray<SalePhoto> {
  readonly entityName = 'SalePhoto' as const;

  constructor(options?: PersistedArrayOptions) {
    super(SalePhoto, options);
  }
}
